/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Global
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { tv } from 'tailwind-variants';
import { Text, useSitecore } from '@sitecore-content-sdk/nextjs';
// Local
import { withStandardComponentWrapper } from 'helpers/HOC';
import { accordionItemTailwindVariants } from './AccordionItem';
import { accordionListTailwindVariants } from './Accordion';
import { AccordionContextProvider, useAccordionContext, useAccordionItemContext } from 'helpers/Context/AccordionContext';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import { useScrollElementIntoView } from 'lib/hooks/useScrollElementIntoView';
import { useLazyLoading } from 'lib/hooks/useLazyLoading';
import useDictionary from 'lib/hooks/useDictionary';
import { getTestProps } from 'lib/testing/utils';
import { createContentViewGTMEvent } from 'lib/utils/gtm-utils';
import { sendGTMEvent } from '@next/third-parties/google';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
// Individual Accordion Item Component
const AccordionItem = ({ item, index })=>{
    const isEditing = useIsEditing();
    // Use the SAME uid as the panels passed to AccordionContextProvider
    const panelUid = item?.id || `accordion-${index}`;
    // DOM id can be namespaced, but must map back to panelUid for context
    const domId = `accordion-block-${panelUid}`;
    const accordionItemButtonRef = useRef(null);
    const accordionItemBodyRef = useRef(null);
    const heading = item.fields?.heading?.value || '';
    const content = item.fields?.content?.value || '';
    const { isOpening, toggleOpen, isVisible, scrollToOpenPanel } = useAccordionItemContext(panelUid, accordionItemBodyRef);
    const handleToggle = useCallback(()=>{
        toggleOpen();
    }, [
        toggleOpen
    ]);
    const scrollCondition = scrollToOpenPanel && isOpening;
    useScrollElementIntoView(accordionItemButtonRef.current, {
        stickyHeaderId: 'header',
        condition: scrollCondition,
        animationElement: accordionItemBodyRef.current,
        scrollTargetId: `${domId}-scroll-target`
    });
    const { base: accordionItemBase, buttonWrapper, headingText, iconWrapper, contentContainer, richTextWrapper: contentText } = accordionItemTailwindVariants({
        isOpening,
        isVisible
    });
    return /*#__PURE__*/ _jsx("div", {
        className: accordionItemBase(),
        "data-component": "authorable/shared/lists/accordion-item",
        ...editingHydrationProps(isEditing),
        ...getTestProps(`component-accordion-item-${index}`),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col gap",
            children: [
                /*#__PURE__*/ _jsxs("button", {
                    ref: accordionItemButtonRef,
                    type: "button",
                    className: buttonWrapper(),
                    onClick: handleToggle,
                    "aria-label": heading,
                    "aria-expanded": isOpening,
                    "aria-controls": `${domId}-content`,
                    id: domId,
                    ...getTestProps(`accordion-item-button-${index}`),
                    children: [
                        /*#__PURE__*/ _jsx(Text, {
                            field: {
                                value: heading
                            },
                            tag: "h3",
                            className: headingText(),
                            ...getTestProps(`accordion-item-heading-${index}`)
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: iconWrapper(),
                            ...getTestProps(`icon-${index}`),
                            children: /*#__PURE__*/ _jsx(SvgIcon, {
                                fill: "none",
                                icon: "chevron-down",
                                size: "xs",
                                viewBox: "0 0 24 24"
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    ref: accordionItemBodyRef,
                    id: `${domId}-content`,
                    className: contentContainer(),
                    "aria-labelledby": domId,
                    role: "region",
                    tabIndex: isOpening ? undefined : -1,
                    "aria-hidden": !isOpening,
                    ...getTestProps(`accordion-item-content-${index}`),
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "overflow-hidden",
                        children: /*#__PURE__*/ _jsx(RichTextWrapper, {
                            field: {
                                value: content
                            },
                            className: contentText(),
                            tabIndex: -1,
                            ...getTestProps(`accordion-item-content-${index}`)
                        })
                    })
                })
            ]
        })
    });
};
// Expand/Collapse All Buttons Component
const AccordionControls = ()=>{
    const { openPanels, panels, expandAll, collapseAll } = useAccordionContext();
    const { getDictionaryValue } = useDictionary();
    const allPanelIds = panels.map((panel)=>panel.uid ?? '').filter(Boolean);
    const allOpen = allPanelIds.length > 0 && allPanelIds.every((id)=>openPanels.some((panel)=>panel.uid === id));
    const someOpen = openPanels.length > 0;
    // Handle expand all with proper state management
    const handleExpandAll = ()=>{
        expandAll();
    };
    // Handle collapse all with proper state management
    const handleCollapseAll = ()=>{
        collapseAll();
    };
    const { expandCollapseBtn, expandCollapseBtnActive, expandCollapseBtnInactive, divider, controlsContainer } = accordionListTailwindVariants();
    return /*#__PURE__*/ _jsxs("div", {
        className: controlsContainer(),
        children: [
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                onClick: handleExpandAll,
                disabled: allOpen,
                className: expandCollapseBtn() + ' ' + (allOpen ? expandCollapseBtnInactive() : expandCollapseBtnActive()),
                "aria-label": "Expand all items",
                children: getDictionaryValue('AccordionExpandAll') || 'Expand All'
            }),
            /*#__PURE__*/ _jsx("span", {
                className: divider()
            }),
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                onClick: handleCollapseAll,
                disabled: !someOpen,
                className: expandCollapseBtn() + ' ' + (!someOpen ? expandCollapseBtnInactive() : expandCollapseBtnActive()),
                "aria-label": "Collapse all items",
                children: getDictionaryValue('AccordionCollapseAll') || 'Collapse All'
            })
        ]
    });
};
const AccordionBlock = (props)=>{
    const { page } = useSitecore();
    const isEditing = page?.mode?.isEditing;
    const { RenderingIdentifier, smallContainer } = props?.params || {};
    const dictionary = useDictionary();
    const isSmallContainer = smallContainer === '1';
    // Lazy loading configuration
    const enableLazyLoading = props?.params?.enableLazyLoading === '1';
    const lazyLoadThreshold = parseFloat(props?.params?.lazyLoadThreshold || '0.1');
    const lazyLoadInitialCount = parseInt(props?.params?.lazyLoadInitialCount || '5', 10);
    const lazyLoadBatchCount = parseInt(props?.params?.lazyLoadBatchCount || '5', 10);
    // Map multilist items to the format expected by AccordionContextProvider
    const safeItems = props?.fields?.accordionItems ?? [];
    // Use reusable lazy loading hook
    // NOTE: Temporary client-side lazy loading using IntersectionObserver. When a data model/API
    // is available for server-driven pagination or partial data loading, this can be replaced with
    // a model-backed approach (e.g., paging/infinite scroll) to avoid rendering large datasets.
    const { visibleItems, hasMore, isLoading, sentinelRef } = useLazyLoading(safeItems, enableLazyLoading, {
        initialCount: lazyLoadInitialCount,
        loadMoreCount: lazyLoadBatchCount,
        threshold: lazyLoadThreshold,
        rootMargin: '100px',
        loadingDelay: 300
    });
    const mappedListItems = visibleItems.map((item, index)=>({
            uid: item?.id || `accordion-${index}`,
            componentName: 'AccordionItem',
            fields: {
                heading: item?.fields?.heading ?? {
                    value: ''
                },
                content: item?.fields?.content ?? {
                    value: ''
                }
            }
        }));
    const singlePanelOpen = props?.params?.singleOpenPanel === '1';
    const scrollToOpenPanel = props?.params?.scrollToOpenPanel === '1';
    const twoColumn = props?.params?.twoColumn === '1';
    const defaultOpenPanels = [];
    if (props?.params?.openByDefault === '1') {
        // Open By Default should only open the first panel
        defaultOpenPanels.push(mappedListItems[0]);
    }
    // Convert HTML to plain text for JSON-LD answers.
    // Must be isomorphic (no window/DOM) so SSR and client produce identical markup.
    const convertHtmlToPlainText = (html)=>{
        if (!html) return '';
        return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    };
    const faqSchema = useMemo(()=>{
        const entities = visibleItems.reduce((acc, item)=>{
            const name = item?.fields?.heading?.value || '';
            const answerHtml = item?.fields?.content?.value || '';
            const text = convertHtmlToPlainText(answerHtml);
            if (name && text) {
                acc.push({
                    '@type': 'Question',
                    name,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text
                    }
                });
            }
            return acc;
        }, []);
        if (entities.length === 0) return null;
        return {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: entities
        };
    }, [
        visibleItems
    ]);
    // GTM Content View Event - Track when more accordion content is loaded via lazy loading
    const previousVisibleCount = useRef(0);
    const isInitialLoad = useRef(true);
    useEffect(()=>{
        if (isInitialLoad.current) {
            // Set initial count but don't fire GTM event on first load
            previousVisibleCount.current = visibleItems.length;
            isInitialLoad.current = false;
        } else if (visibleItems.length > previousVisibleCount.current) {
            // Only fire GTM event when lazy loading adds more items
            const gtmEvent = createContentViewGTMEvent({
                contentType: 'Article'
            });
            sendGTMEvent(gtmEvent);
            previousVisibleCount.current = visibleItems.length;
        }
    }, [
        visibleItems.length
    ]);
    const { base, container, twoColumnContainer, twoColumnLeft, twoColumnRight, lazyLoadingSentinel, lazyLoadingContent, lazyLoadingSpinner, lazyLoadingText, lazyLoadingPlaceholder } = accordionBlockTailwindVariants({
        isSmallContainer
    });
    const { controls } = accordionListTailwindVariants();
    return /*#__PURE__*/ _jsxs("section", {
        className: base(),
        "data-component": "authorable/shared/lists/accordion-block",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`component-accordion-block-${props?.rendering?.uid}`),
        children: [
            /*#__PURE__*/ _jsxs(AccordionContextProvider, {
                panels: mappedListItems,
                singleOpenPanel: singlePanelOpen,
                scrollToOpenPanel: scrollToOpenPanel,
                defaultOpenPanels: defaultOpenPanels,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: controls(),
                        children: !singlePanelOpen && /*#__PURE__*/ _jsx(AccordionControls, {})
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: container(),
                        children: [
                            twoColumn ? // Two column layout with independent heights
                            /*#__PURE__*/ _jsxs("div", {
                                className: twoColumnContainer(),
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: twoColumnLeft(),
                                        children: visibleItems.filter((_, index)=>index % 2 === 0).map((item, index)=>/*#__PURE__*/ _jsx(AccordionItem, {
                                                item: item,
                                                index: index * 2
                                            }, item.id || index * 2))
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: twoColumnRight(),
                                        children: visibleItems.filter((_, index)=>index % 2 === 1).map((item, index)=>/*#__PURE__*/ _jsx(AccordionItem, {
                                                item: item,
                                                index: index * 2 + 1
                                            }, item.id || index * 2 + 1))
                                    })
                                ]
                            }) : // Single column layout
                            visibleItems.map((item, index)=>/*#__PURE__*/ _jsx(AccordionItem, {
                                    item: item,
                                    index: index
                                }, item.id || index)),
                            enableLazyLoading && hasMore && /*#__PURE__*/ _jsx("div", {
                                ref: sentinelRef,
                                className: lazyLoadingSentinel(),
                                children: isLoading ? /*#__PURE__*/ _jsxs("div", {
                                    className: lazyLoadingContent(),
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: lazyLoadingSpinner()
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: lazyLoadingText(),
                                            children: dictionary.getDictionaryValue('AccordionLoadingMore') || 'Loading more...'
                                        })
                                    ]
                                }) : /*#__PURE__*/ _jsx("div", {
                                    className: lazyLoadingPlaceholder()
                                })
                            })
                        ]
                    })
                ]
            }),
            !isEditing && faqSchema && /*#__PURE__*/ _jsx("script", {
                type: "application/ld+json",
                dangerouslySetInnerHTML: {
                    __html: JSON.stringify(faqSchema)
                }
            })
        ]
    });
};
export const Default = withStandardComponentWrapper(AccordionBlock, false);
const accordionBlockTailwindVariants = tv({
    slots: {
        base: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-8'
        ],
        container: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-8'
        ],
        twoColumnContainer: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-8',
            'md:flex-row',
            'md:gap-spacing-spacing-24'
        ],
        twoColumnLeft: [
            'space-y-spacing-spacing-8',
            'md:flex-1'
        ],
        twoColumnRight: [
            'space-y-spacing-spacing-8',
            'md:flex-1'
        ],
        lazyLoadingSentinel: [
            'flex',
            'justify-center',
            'py-spacing-spacing-8'
        ],
        lazyLoadingContent: [
            'flex',
            'items-center',
            'gap-spacing-spacing-4'
        ],
        lazyLoadingSpinner: [
            'animate-spin',
            'rounded-full',
            'h-6',
            'w-6',
            'border-b-2',
            'border-component-accordion-item-border'
        ],
        lazyLoadingText: [
            'text-typography-font-size-body-medium',
            'text-color-text-text'
        ],
        lazyLoadingPlaceholder: [
            'h-4'
        ]
    },
    variants: {
        isSmallContainer: {
            true: {
                base: [
                    'contentWrapper'
                ]
            },
            false: {
                base: [
                    ''
                ]
            }
        }
    },
    defaultVariants: {
        isSmallContainer: false
    }
});
